import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm, usePage } from "@inertiajs/react"
import { IconArrowLeft, IconInfoCircle, IconSparkles } from "@tabler/icons-react"
import { useRef } from "react"

interface Props {
  [key: string]: unknown
  is_development: boolean
}

export default function TimeEntryImport() {
  const { is_development } = usePage<Props>().props
  const fileInputRef = useRef<HTMLInputElement>(null)
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone
  const { data, setData, post, processing } = useForm<{
    csv_file: File | null
    timezone: string
  }>({
    csv_file: null,
    timezone: browserTz,
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post("/time_entries/process_import", {
      forceFormData: true,
    })
  }

  return (
    <>
      <Head title="Import Time Entries - Operations Center" />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/time_entries">Time Tracking</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Import</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href="/time_entries">
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <h1 className="text-lg font-semibold tracking-tight">Import Time Entries from CSV</h1>
          </div>
        </div>

        <div className="max-w-2xl">
          <form onSubmit={handleSubmit}>
            <Card>
              <CardHeader>
                <CardTitle>Upload CSV</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="csv_file">Select CSV File</Label>
                  <input
                    ref={fileInputRef}
                    id="csv_file"
                    type="file"
                    accept=".csv"
                    required
                    onChange={(e) => setData("csv_file", e.target.files?.[0] || null)}
                    className="border-input bg-background block w-full rounded-md border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timezone">CSV Timezone</Label>
                  <Select value={data.timezone} onValueChange={(v) => setData("timezone", v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="America/Montevideo">America/Montevideo (UTC-3)</SelectItem>
                      <SelectItem value="America/Argentina/Buenos_Aires">America/Buenos Aires (UTC-3)</SelectItem>
                      <SelectItem value="America/Sao_Paulo">America/Sao Paulo (UTC-3)</SelectItem>
                      <SelectItem value="America/New_York">America/New York (UTC-5/4)</SelectItem>
                      <SelectItem value="America/Chicago">America/Chicago (UTC-6/5)</SelectItem>
                      <SelectItem value="America/Los_Angeles">America/Los Angeles (UTC-8/7)</SelectItem>
                      <SelectItem value="Europe/London">Europe/London (UTC+0/1)</SelectItem>
                      <SelectItem value="Europe/Madrid">Europe/Madrid (UTC+1/2)</SelectItem>
                      <SelectItem value="UTC">UTC</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-muted-foreground text-xs">
                    The timezone of the times in your CSV file (auto-detected from your browser).
                  </p>
                </div>

                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/30">
                  <div className="flex gap-3">
                    <IconInfoCircle className="mt-0.5 h-5 w-5 shrink-0 text-blue-500" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-800 dark:text-blue-300">CSV Format (Toggl Export)</p>
                      <div className="mt-2 space-y-2 text-blue-700 dark:text-blue-400">
                        <p>Your CSV should have these column headers:</p>
                        <ul className="list-inside list-disc space-y-1">
                          <li>
                            <strong>Email</strong> — user email to match entries (required)
                          </li>
                          <li>
                            <strong>Description</strong> — task description
                          </li>
                          <li>
                            <strong>Project</strong> — project name
                          </li>
                          <li>
                            <strong>Billable</strong> — Yes/No
                          </li>
                          <li>
                            <strong>Duration</strong> — H:MM:SS format
                          </li>
                          <li>
                            <strong>Start date</strong> — YYYY-MM-DD
                          </li>
                          <li>
                            <strong>Start time</strong> — HH:MM:SS
                          </li>
                          <li>
                            <strong>Stop date</strong> — YYYY-MM-DD
                          </li>
                          <li>
                            <strong>Stop time</strong> — HH:MM:SS
                          </li>
                          <li>
                            <strong>Member</strong> — member name (used for auto-creation)
                          </li>
                          <li>
                            <strong>Tags</strong> — ignored
                          </li>
                        </ul>

                        <p className="pt-2 font-medium">Example:</p>
                        <div className="overflow-x-auto rounded border border-blue-200 bg-white p-2 font-mono text-xs dark:border-blue-800 dark:bg-gray-900">
                          &quot;Description&quot;,&quot;Billable&quot;,&quot;Duration&quot;,&quot;Member&quot;,&quot;Email&quot;,&quot;Project&quot;,&quot;Tags&quot;,&quot;Start
                          date&quot;,&quot;Start time&quot;,&quot;Stop date&quot;,&quot;Stop time&quot;
                          <br />
                          &quot;CR
                          changes&quot;,&quot;Yes&quot;,&quot;0:30:00&quot;,&quot;Joaquin&quot;,&quot;joaquin@example.com&quot;,&quot;my-project&quot;,&quot;-&quot;,&quot;2026-03-04&quot;,&quot;12:00:00&quot;,&quot;2026-03-04&quot;,&quot;12:30:00&quot;
                        </div>

                        <p className="pt-2">
                          Users are matched by <strong>Email</strong>. Projects are matched by <strong>name</strong>{" "}
                          (case-insensitive). Rows with empty Email are skipped. Rows with no Project (or &quot;-&quot;)
                          are skipped.
                        </p>
                      </div>

                      {is_development && (
                        <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-900 dark:bg-green-950/30">
                          <div className="flex items-start gap-2">
                            <IconSparkles className="mt-0.5 h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
                            <div>
                              <p className="font-medium text-green-800 dark:text-green-300">
                                Development Mode: Auto-creation enabled
                              </p>
                              <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-green-700 dark:text-green-400">
                                <li>Users not found by email will be created automatically</li>
                                <li>
                                  Projects not found by name will be created under an &quot;Imported&quot; customer
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="justify-end gap-2 border-t">
                <Button variant="ghost" asChild>
                  <a href="/time_entries">Cancel</a>
                </Button>
                <Button type="submit" disabled={processing || !data.csv_file}>
                  {processing ? "Importing..." : "Import CSV"}
                </Button>
              </CardFooter>
            </Card>
          </form>
        </div>
      </div>
    </>
  )
}

TimeEntryImport.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
